import Button from '@rio-cloud/rio-uikit/Button';
import SortDirection from '@rio-cloud/rio-uikit/utils/SortUtils';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useAppSelector } from '@/configuration/setup/hooks';
import { useSendQuestionMutation } from '@/services/chatApi/chatApi';
import { selectAllLoadedThreads, selectIsHistoryOfThreadsFetching, selectPagination } from '@/store/thread/threadSlice';
import { isSortDirection } from '@/utils/sortHelper/sortHelper';

import { ThreadsInfo } from './Style';
import { ThreadsListControlBarProps } from './types';

const texts = {
  sort: 'chatPage.chatThreadList.sort.label',
  threads: 'chatPage.chatThreadList.threads.label',
} as const;

const ThreadsListControlBar = ({ changeSortOrder }: ThreadsListControlBarProps) => {
  const intl = useIntl();
  const isLoading = useAppSelector(selectIsHistoryOfThreadsFetching);
  const allLoadedThreads = useAppSelector(selectAllLoadedThreads);
  const pagination = useAppSelector(selectPagination);

  // INFO: Defines sort order, sets descending order by default
  const sortOrder = useMemo(
    () => (isSortDirection(pagination?.sortOrder) ? pagination.sortOrder : SortDirection.DESCENDING),
    [pagination.sortOrder],
  );

  const [, { isLoading: isSendMessageLoading }] = useSendQuestionMutation();

  const isChangeSortorderDisabled = isLoading || isSendMessageLoading;

  const buttonIcon = sortOrder === SortDirection.DESCENDING ? 'icon-sort-by-attributes' : 'icon-sort-by-attributes-alt';
  const sortLabel = intl.formatMessage({ id: texts.sort });

  return (
    <div className="display-flex align-items-center justify-content-end padding-x-20 bg-white">
      <ThreadsInfo className="margin-0">
        {intl.formatMessage({ id: texts.threads })} {allLoadedThreads}/{pagination.totalThreads}
      </ThreadsInfo>
      <Button
        type="button"
        bsStyle="primary"
        variant="link"
        iconOnly
        iconName={buttonIcon}
        aria-label={sortLabel}
        tooltip={sortLabel}
        disabled={isChangeSortorderDisabled}
        onClick={changeSortOrder}
        data-testid="thread-sort-toggle-button"
      />
    </div>
  );
};

export default ThreadsListControlBar;
